/*
 * evacuation-plan/app.js — страница /evacuation-plan: состояние, панели, выдача.
 *
 * Работа человека — ПЛАН (проект): переживает F5 и крах через __toolDraft.editor
 * (подложка — байтами). Единица суточного счёта — id плана, штампованный при
 * создании: другой лист, масштаб, плитки, пачка листов по точкам крепления и
 * повторная выгрузка того же плана — та же единица (решение волны 29.10).
 * Предпросмотр и проверка не списывают ничего.
 */
import * as Mo from './model.js';
import * as G from './geom.js';
import { analyze, checkManualRoute, herePoint } from './route.js';
import { layoutSheet, approxMeasure, mountTheta } from './sheet.js';
import { sheetSvg } from './svg.js';
import { resolveNorms, SHEETS, SCALES, NORMS_CHECKED, RU_RULES, LOCALE_COUNTRY } from './norms.js';
import { sectionsFor, defaultSections } from './text.js';
import { runChecks, summarize } from './checks.js';
import { createEditor } from './editor.js';
import { createTextEngine } from './type.js';
import { buildSheetsPdf, buildTilesPdf, planTiles } from './pdf.js';
import { PALETTE, iconParts } from './icons.js';
import { rgbOf } from './ink.js';
import * as Tr from './trace.js';
import { detectQuad } from '../document-scanner/cv-detect.js';
import { warp } from '../document-scanner/cv-warp.js';
import { downloadIcs } from '../ics-simple.js';

const C = window.__EVAC || {};
const SLUG = 'evacuation-plan';
const LOC = C.locale || 'ru';
const RTL_LANGS = { ar: 1 };
const $ = (s, r) => (r || document).querySelector(s);
const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));

function get(obj, path) { return String(path).split('.').reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj); }
function t(key, vars) {
    const s = get(C.t, key);
    if (s == null) return key;
    return String(s).replace(/:([a-z_]+)/g, (m, k) => (vars && vars[k] != null ? String(vars[k]) : m));
}
function intl() { return (window.__intlLocale && window.__intlLocale()) || LOC; }
function nf(v, d) { try { return new Intl.NumberFormat(intl(), { maximumFractionDigits: d == null ? 1 : d }).format(v); } catch (_) { return String(v); } }
function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
function ae(f, p) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, f }, p || {})); } catch (_) { /* телеметрия не мешает работе */ } }
function stage(s, m) { try { window.__stage && window.__stage(s, m); } catch (_) { /* то же */ } }
function toast(msg, type) { if (window.__toast) window.__toast(msg, type || 'info'); }
/* Отказ, который человек видит тостом, — ещё и событие: почему не получилось (ключ, без ввода человека). */
function refuse(key, type) { toast(t('ui.' + key), type || 'warning'); ae('refused', { why: key }); }

/* ── состояние ───────────────────────────────────────────────────────── */
let plan = Mo.newPlan({ country: C.country || LOCALE_COUNTRY[LOC] || 'ISO' });
plan.meta.lang = LOC;
let underlayBlob = null, underlayUrl = null;
let an = null;
let engine = null, enginePromise = null;
let sheetIdx = 0;
let undoStack = [], redoStack = [];
let snap = JSON.stringify(plan);
let measurePurpose = null;
let lastMeasure = null;
let ed = null;           // черновик
let dirtyEdits = 0;

/* ── редактор ─────────────────────────────────────────────────────────── */
const svgEl = $('#ep-svg');
const editor = createEditor(svgEl, {
    plan: () => plan,
    locale: intl(),
    commit: (label) => commit(label),
    live: () => { /* только перерисовка — делает сам редактор */ },
    select: (id) => renderProps(id),
    analysis: () => an,
    routes: () => editorRoutes(),
    routesAuto: () => plan.routes.mode === 'auto' && plan.routes.confirmed !== (an && an.sig),
    underlayUrl: () => underlayUrl,
    itemType: () => currentItem,
    wallKind: () => currentWallKind,
    routeKind: () => currentRouteKind,
    onMeasure: (a, b) => onMeasure(a, b),
    onTool: (tool) => onTool(tool),
    say: (k) => refuse(k, 'info'),
});
let currentItem = 'extinguisher';
let currentWallKind = 'ext';
let currentRouteKind = 'main';

function commit(label) {
    const s = JSON.stringify(plan);
    if (s === snap) return;
    undoStack.push(snap);
    if (undoStack.length > 120) undoStack.shift();
    redoStack = [];
    snap = s;
    dirtyEdits++;
    if (ed) ed.touch(true);
    changed();
    if (label && label !== 'move') ae('edit', { what: label });
}
function changed(fit) {
    editor.render();
    if (fit) editor.fit();
    renderProps(editor.selection());
    syncControls();
    scheduleAnalysis();
    updateUndo();
}
function undo() {
    if (!undoStack.length) return;
    redoStack.push(snap);
    snap = undoStack.pop();
    plan = Mo.normalize(JSON.parse(snap));
    editor.setSelection(null);
    if (ed) ed.touch(true);
    changed();
}
function redo() {
    if (!redoStack.length) return;
    undoStack.push(snap);
    snap = redoStack.pop();
    plan = Mo.normalize(JSON.parse(snap));
    if (ed) ed.touch(true);
    changed();
}
function updateUndo() {
    const u = $('#ep-undo'), r = $('#ep-redo');
    if (u) u.disabled = !undoStack.length;
    if (r) r.disabled = !redoStack.length;
}
/* Полная замена плана (образец, черновик, новый): история начинается заново. */
function replacePlan(p, opts) {
    plan = Mo.normalize(p);
    if (!plan.meta.lang) plan.meta.lang = LOC;
    undoStack = []; redoStack = [];
    snap = JSON.stringify(plan);
    an = null;
    editor.setSelection(null);
    changed(!(opts && opts.keepView));
}

/* ── разбор и пути (с задержкой: правка не ждёт пересчёта) ──────────── */
let anTimer = 0;
function scheduleAnalysis() {
    clearTimeout(anTimer);
    anTimer = setTimeout(runAnalysis, 160);
}
function runAnalysis() {
    try {
        an = plan.walls.length ? analyze(plan) : null;
    } catch (e) {
        an = null;
        window.__handleError && window.__handleError(e, 'evac_analyze', { say: t('ui.err_analyze') });
    }
    editor.render();
    renderRoutesPanel();
    renderSheet();
}

function manualTree() {
    const out = { main: [], emergency: [] };
    for (const r of plan.routes.manual) {
        const v = checkManualRoute(plan, r);
        out[r.kind === 'emergency' ? 'emergency' : 'main'].push({ pts: r.pts, kind: r.kind, end: v.endsAtExit, exit: null });
    }
    return out;
}
function editorRoutes() {
    if (plan.routes.mode === 'manual') return manualTree();
    if (!an) return null;
    return an.tree(plan.mounts.map((m) => m.id));
}
function sheetTree(mount) {
    if (plan.routes.mode === 'manual') return manualTree();
    if (!an) return { main: [], emergency: [] };
    return an.tree(mount ? [mount.id] : []);
}

/* ── лист ─────────────────────────────────────────────────────────────── */
function norms() { return resolveNorms(plan.country); }
/* Двуязычный лист (9.3.9.15) по умолчанию на размер больше: текст двух языков
   при прописных 3 мм в A3 не помещается (замер 30.10: ar+en на A3 — 26 строк за краем). */
const BUMP = { a4: 'a3', a3: 'a2', '400x300': '600x400', letter: 'tabloid' };
function formatOf() {
    const n = norms();
    if (plan.sheet.format) return plan.sheet.format;
    const f = n.profile.defaultSheet[plan.kind] || '600x400';
    return plan.meta.lang2 && plan.meta.lang2 !== (plan.meta.lang || LOC) && BUMP[f] ? BUMP[f] : f;
}
function sheetStrings(lang) { return (C.sheets && (C.sheets[lang] || C.sheets[LOC] || C.sheets.en)) || {}; }
function ctxFor(mount, withEngine, section) {
    const n = norms();
    const L1 = plan.meta.lang || LOC, L2 = plan.meta.lang2 || '';
    const S = sheetStrings(L1), S2 = L2 && L2 !== L1 ? sheetStrings(L2) : null;
    const phones = n.country.phones;
    const sections = sectionsFor(plan, S.text, phones);
    const sections2 = S2 ? defaultSections(S2.text, plan.meta.objectType, plan.meta.night, phones, plan.equipment) : [];
    return {
        norms: n, an, mount, tree: sheetTree(mount), format: formatOf(), orient: plan.sheet.orient,
        den: +plan.sheet.scale || 0, mode: plan.sheet.mode === 'pl' ? 'pl' : 'paper',
        S, S2, sections, sections2, rtl: !!RTL_LANGS[L1], rtl2: !!RTL_LANGS[L2],
        measure: withEngine && engine ? engine.measure : approxMeasure,
        locale: L1 === 'ar' ? 'ar-u-nu-latn' : L1,
        section: section || null, planSections: plan.sections || [],
    };
}
/* Листы: по точке крепления; в секционном плане лист точки показывает её секцию,
   а без точек — по листу на секцию (п. 9.3.9.2). */
function sectionMode() { return plan.kind === 'section' && (plan.sections || []).length > 0; }
function sheetList() {
    const sm = sectionMode();
    if (plan.mounts.length) return plan.mounts.map((m) => ({ mount: m, section: sm ? Mo.sectionAt(plan, herePoint(m)) : null }));
    if (sm) return plan.sections.map((sc) => ({ mount: null, section: sc }));
    return [{ mount: null, section: null }];
}
function layoutAll(withEngine) {
    return sheetList().map((e) => ({ mount: e.mount, section: e.section, sheet: layoutSheet(plan, ctxFor(e.mount, withEngine, e.section)) }));
}

let sheetTimer = 0;
function renderSheet() {
    clearTimeout(sheetTimer);
    sheetTimer = setTimeout(renderSheetNow, 60);
}
function renderSheetNow() {
    const box = $('#ep-sheet');
    if (!box) return;
    renderMountChips();
    if (!plan.walls.length) {
        $('#ep-big-go').hidden = true;
        box.innerHTML = '<p class="ep-empty">' + esc(t('ui.sheet_empty')) + '</p>';
        renderChecks(null);
        return;
    }
    let all;
    try { all = layoutAll(true); } catch (e) {
        window.__handleError && window.__handleError(e, 'evac_layout', { say: t('ui.err_layout') });
        return;
    }
    sheetIdx = Math.min(sheetIdx, all.length - 1);
    const cur = all[sheetIdx].sheet;
    const svg = sheetSvg(cur, {
        outline: engine ? engine.outline : null,
        fontFamily: '"PTF noto", "Noto Sans", Arial, sans-serif',
        className: 'ep-sheet-svg',
        title: t('ui.sheet_aria'),
    });
    box.innerHTML = svg;
    box.style.aspectRatio = cur.w + ' / ' + cur.h;
    lastAR = cur.w / cur.h;
    const fmt = formatOf();
    const deg = Math.round(cur.theta * 180 / Math.PI);
    const info = [t('ui.fmt.' + fmt), '1:' + cur.den];
    if (all[sheetIdx].mount) info.push(t('ui.rotated', { deg: ((deg % 360) + 360) % 360 }));
    if (plan.sheet.mode === 'tiles') {
        const tl = planTiles(cur, plan.sheet.tile || 'a4', 10);
        if (tl) info.push(t('ui.tiles_n', { n: tl.count, paper: t('ui.fmt.' + (plan.sheet.tile || 'a4')) }));
    }
    $('#ep-sheet-info').textContent = info.join(' · ');
    $('#ep-big-go').hidden = false;
    if (bigWs && bigWs.active()) paintBig();
    renderChecks(all);
    renderExport(all);
    if (!engine) ensureEngine().then(() => renderSheet()).catch(() => { /* предпросмотр останется с <text> */ });
}

/* ── лист крупно ───────────────────────────────────────────────────────── */
let bigWs = null, bigZoom = 1, lastAR = 1.5;
function paintBig() {
    const src = $('#ep-sheet svg');
    const dst = $('#ep-big-sheet');
    if (!src || !dst) return;
    dst.innerHTML = src.outerHTML;
    const sc = $('#ep-big-scroll');
    const r = sc.getBoundingClientRect();
    // Масштаб 1 — лист целиком в окне; дальше крупнее, с прокруткой внутри слоя.
    const fitW = Math.min(r.width - 8, (r.height - 8) * (lastAR || 1.5));
    dst.style.width = Math.max(200, Math.round(fitW * bigZoom)) + 'px';
    $('#ep-big-t').textContent = $('#ep-sheet-info').textContent;
}
function openBig() {
    const el = $('#ep-big');
    if (!el || !window.__wholeScreen) return;
    if (!bigWs) {
        bigWs = window.__wholeScreen(el, {
            key: 'evac-sheet',
            onChange: (on) => { el.hidden = !on; if (on) { bigZoom = 1; requestAnimationFrame(paintBig); } else $('#ep-big-sheet').innerHTML = ''; },
        });
    }
    el.hidden = false;
    bigWs.enter();
    ae('sheet_big');
}
function zoomBig(k) {
    const sc = $('#ep-big-scroll');
    const cx = (sc.scrollLeft + sc.clientWidth / 2) / Math.max(1, sc.scrollWidth), cy = (sc.scrollTop + sc.clientHeight / 2) / Math.max(1, sc.scrollHeight);
    bigZoom = Math.max(1, Math.min(8, bigZoom * k));
    paintBig();
    sc.scrollLeft = cx * sc.scrollWidth - sc.clientWidth / 2;
    sc.scrollTop = cy * sc.scrollHeight - sc.clientHeight / 2;
}

function ensureEngine() {
    if (engine) return Promise.resolve(engine);
    if (enginePromise) return enginePromise;
    const strings = [];
    const walk = (o) => { if (o == null) return; if (typeof o === 'string') strings.push(o); else if (typeof o === 'object') for (const k in o) walk(o[k]); };
    walk(sheetStrings(plan.meta.lang || LOC));
    if (plan.meta.lang2) walk(sheetStrings(plan.meta.lang2));
    walk(plan.meta); walk(plan.labels.map((l) => l.text)); walk(plan.mounts.map((m) => m.label)); walk(plan.text);
    enginePromise = createTextEngine(strings.join(' ') + ' 0123456789:·–—«»()').then((e) => { engine = e; return e; })
        .catch((e) => { enginePromise = null; throw e; });
    return enginePromise;
}

function chipLabel(e, i) {
    const sc = e.section && String(e.section.name || '').trim() ? ' · ' + t('ui.section_n', { s: e.section.name }) : '';
    if (e.mount) return (i + 1) + ' · ' + (e.mount.label || t('ui.mount_noname')) + sc;
    return t('ui.section_n', { s: (e.section && e.section.name) || i + 1 });
}
function renderMountChips() {
    const box = $('#ep-mounts');
    if (!box) return;
    const list = sheetList();
    if (!plan.mounts.length && !sectionMode()) { box.innerHTML = '<span class="tc-hint">' + esc(t('ui.no_mounts')) + '</span>'; return; }
    box.innerHTML = list.map((e, i) => '<button type="button" class="tc-chip' + (i === sheetIdx ? ' on' : '') + '" data-sheet="' + i + '" aria-pressed="' + (i === sheetIdx) + '">' +
        esc(chipLabel(e, i)) + '</button>').join('');
}

/* ── проверка ─────────────────────────────────────────────────────────── */
function clauseText(c) {
    if (!c || c === '—') return '';
    const n = norms();
    if (c === 'ППР') return RU_RULES.code;
    return n.profile.id === 'ru' ? t('ui.clause_ru', { c }) : t('ui.clause_iso', { c });
}
let lastChecks = [];
function renderChecks(all) {
    const box = $('#ep-check');
    if (!box) return;
    if (!all) { box.innerHTML = ''; $('#ep-check-sum').textContent = ''; lastChecks = []; return; }
    const res = runChecks(plan, { norms: norms(), an, sheets: all, format: formatOf(), sections: ctxFor(null).sections, mode: plan.sheet.mode === 'pl' ? 'pl' : 'paper' });
    lastChecks = res;
    const s = summarize(res);
    $('#ep-check-sum').innerHTML = esc(t('ui.check_sum', { auto: s.auto, e: s.error, w: s.warn, m: s.manual }));
    const order = { error: 0, warn: 1, info: 2, ok: 3, manual: 4 };
    const icon = { error: '✖', warn: '⚠', info: 'ℹ', ok: '✔', manual: '👤' };
    res.sort((a, b) => order[a.level] - order[b.level]);
    let lastLevel = '';
    box.innerHTML = res.map((r, i) => {
        let head = '';
        if (r.level === 'manual' && lastLevel !== 'manual') head = '<li class="ep-ck-h">' + esc(t('ui.check_manual_h')) + '</li>';
        lastLevel = r.level;
        const params = Object.assign({}, r.params);
        /* Числа — по языку страницы: «1,5 мм», а не «1.5 мм» (проверка печатала числа как есть). */
        for (const k in params) if (typeof params[k] === 'number') params[k] = numStr(params[k]);
        if (params.list) params.list = params.list.map((k) => t('sheet_sym.' + k)).join(', ');
        if (params.side) params.side = t('ui.side_' + params.side);
        if (params.lang) params.lang = t('ui.lang.' + params.lang);
        const cl = clauseText(r.clause);
        const jump = (params.ids && params.ids.length) || (params.seeds && params.seeds.length) ? ' data-ck="' + i + '"' : '';
        const fx = fixFor(r);
        return head + '<li class="ep-ck ep-ck-' + r.level + '"' + jump + '><span class="ep-ck-i" aria-hidden="true">' + icon[r.level] + '</span><span class="ep-ck-t">' +
            esc(t('chk.' + r.key, params)) + (cl ? ' <span class="ep-ck-c">' + esc(cl) + '</span>' : '') +
            (fx ? ' <button type="button" class="tc-btn sm ghost ep-ck-fix" data-fix="' + esc(fx.kind) + '" data-v="' + esc(fx.v || '') + '">' + esc(fx.label) + '</button>' : '') + '</span></li>';
    }).join('');
    const btn = $('#ep-pdf');
    if (btn) btn.setAttribute('data-errors', String(s.error));
}

function numStr(v) {
    try { return new Intl.NumberFormat(intl(), { maximumFractionDigits: 2, useGrouping: false }).format(v); } catch (_) { return String(v); }
}
/* Строка проверки, которую можно исправить одним нажатием: второй язык листа,
   ночной вариант текста, составитель (поле в «Данных на листе»). */
function fixFor(r) {
    if (r.key === 'country_lang2' && r.params.lang) return { kind: 'lang2', v: r.params.lang, label: t('ui.fix_lang2', { lang: t('ui.lang.' + r.params.lang) }) };
    if (r.key === 'hotel_lang2' && (plan.meta.lang || LOC) !== 'en') return { kind: 'lang2', v: 'en', label: t('ui.fix_lang2', { lang: t('ui.lang.en') }) };
    if (r.key === 'hotel_night') return { kind: 'night', label: t('ui.fix_night') };
    if (r.key === 'no_developer') return { kind: 'field', v: 'ep-m-developer', label: t('ui.fix_field') };
    if (r.key === 'no_floor') return { kind: 'field', v: 'ep-m-floor', label: t('ui.fix_field') };
    return null;
}
function applyFix(b) {
    const kind = b.dataset.fix, v = b.dataset.v || '';
    if (kind === 'lang2' && v) { plan.meta.lang2 = v; engine = null; enginePromise = null; ae('check_fix', { k: 'lang2' }); commit('meta_lang2'); return; }
    if (kind === 'night') { plan.meta.night = true; ae('check_fix', { k: 'night' }); commit('night'); return; }
    if (kind === 'field') {
        const el = $('#' + v);
        if (!el) return;
        const dr = el.closest('details');
        if (dr) dr.open = true;
        el.focus({ preventScroll: true });
        if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
        ae('check_fix', { k: v });
    }
}

/* ── пути: подтверждение и ручная правка ──────────────────────────────── */
function renderRoutesPanel() {
    const st = $('#ep-routes-state');
    if (!st) return;
    const manual = plan.routes.mode === 'manual';
    $('#ep-routes-ok').hidden = manual || !an || plan.routes.confirmed === (an && an.sig) || !an.exits.main;
    $('#ep-routes-manual').hidden = manual || !an;
    $('#ep-routes-auto').hidden = !manual;
    $('#ep-rkind').hidden = !manual;
    $$('[data-tool="route"]').forEach((b) => { b.hidden = !manual; });
    let msg;
    if (!plan.walls.length) msg = t('ui.routes_none');
    else if (!an) msg = t('ui.routes_wait');
    else if (!an.closed) msg = t('ui.routes_open_contour');
    else if (!an.exits.main) msg = t('ui.routes_no_exit');
    else if (manual) msg = t('ui.routes_manual', { n: plan.routes.manual.length });
    else if (plan.routes.confirmed === an.sig) msg = t('ui.routes_confirmed');
    else msg = t('ui.routes_auto');
    st.textContent = msg;
    st.className = 'tc-msg ' + (!an || !an.closed || !an.exits.main ? 'warn' : manual || plan.routes.confirmed === an.sig ? 'good' : 'warn');
}

/* ── свойства выделенного ─────────────────────────────────────────────── */
function field(label, html) { return '<label class="tc-f"><span class="tc-f-l">' + esc(label) + '</span>' + html + '</label>'; }
function numIn(id, v, step) { return '<input class="tc-in" id="' + id + '" inputmode="decimal" autocomplete="off" value="' + esc(nf(v, 3)) + '" data-step="' + (step || 0.1) + '">'; }
function parseNum(s) { const v = parseFloat(String(s).replace(/\s/g, '').replace(',', '.')); return isFinite(v) ? v : NaN; }
function seg(name, opts, cur) {
    return '<div class="tc-seg" role="group">' + opts.map(([v, l]) => '<button type="button" class="tc-seg-b' + (v === cur ? ' on' : '') + '" data-' + name + '="' + v + '" aria-pressed="' + (v === cur) + '">' + esc(l) + '</button>').join('') + '</div>';
}
let propsFor = null;
function renderProps(id) {
    const box = $('#ep-props');
    if (!box) return;
    // Поле свойств в фокусе у того же объекта — не пересобирать (курсор и набор живы).
    if (id && id === propsFor && box.contains(document.activeElement)) return;
    propsFor = id || null;
    const f = id ? Mo.findById(plan, id) : null;
    if (!f) {
        if (editor.getTool() === 'wall') {
            box.innerHTML = '<div class="ep-drawbar">' + field(t('ui.len_m'), '<input class="tc-in" id="ep-len" inputmode="decimal" autocomplete="off" placeholder="4,5">') +
                field(t('ui.angle'), '<input class="tc-in" id="ep-ang" inputmode="decimal" autocomplete="off" placeholder="0 / 90">') +
                '<button type="button" class="tc-btn sm" id="ep-len-go">' + esc(t('ui.add_segment')) + '</button><button type="button" class="tc-btn sm ghost" id="ep-finish">' + esc(t('ui.finish')) + '</button></div>';
        } else box.innerHTML = '';
        return;
    }
    const o = f.obj;
    let h = '<div class="ep-props-row">';
    if (f.kind === 'walls') {
        h += '<span class="ep-props-k">' + esc(t('ui.obj_wall')) + '</span>';
        h += field(t('ui.len_m'), numIn('ep-p-len', G.wallLength(o), 0.05));
        h += field(t('ui.wall_kind'), seg('wk', [['ext', t('ui.wall_ext')], ['int', t('ui.wall_int')]], o.kind));
        h += field(t('ui.thick_m'), numIn('ep-p-th', o.th, 0.01));
    } else if (f.kind === 'openings') {
        h += '<span class="ep-props-k">' + esc(t('ui.obj_' + o.type)) + '</span>';
        h += field(t('ui.width_m'), numIn('ep-p-w', o.w, 0.05));
        h += field(t('ui.open_type'), seg('ot', [['door', t('ui.obj_door')], ['opening', t('ui.obj_opening')], ['window', t('ui.obj_window')]], o.type));
        if (o.type !== 'window') h += field(t('ui.exit_kind'), seg('ex', [['', t('ui.exit_none')], ['main', t('ui.exit_main')], ['emergency', t('ui.exit_emergency')]], o.exit || ''));
        if (o.type === 'door') h += '<button type="button" class="tc-btn sm ghost" id="ep-p-flip">' + esc(t('ui.flip_side')) + '</button><button type="button" class="tc-btn sm ghost" id="ep-p-hinge">' + esc(t('ui.flip_hinge')) + '</button>';
        if (o.exit) h += '<label class="ep-check"><input type="checkbox" id="ep-p-mgn"' + (o.mgn ? ' checked' : '') + '> ' + esc(t('ui.exit_mgn')) + '</label>';
    } else if (f.kind === 'items') {
        h += '<span class="ep-props-k">' + esc(t('sheet_sym.' + o.type)) + '</span>';
        if (o.type === 'stairs' || o.type === 'lift') {
            h += field(t('ui.width_m'), numIn('ep-p-iw', o.w, 0.1)) + field(t('ui.depth_m'), numIn('ep-p-ih', o.h, 0.1));
            h += '<button type="button" class="tc-btn sm ghost" id="ep-p-rot">↻ 90°</button>';
        }
    } else if (f.kind === 'labels') {
        h += field(t('ui.obj_label'), '<input class="tc-in" id="ep-p-text" autocomplete="off" value="' + esc(o.text) + '" placeholder="' + esc(t('ui.label_ph')) + '">');
    } else if (f.kind === 'mounts') {
        const i = plan.mounts.indexOf(o);
        h += '<span class="ep-props-k">' + esc(t('ui.obj_mount', { n: i + 1 })) + '</span>';
        h += field(t('ui.mount_label'), '<input class="tc-in" id="ep-p-mlabel" autocomplete="off" value="' + esc(o.label) + '" placeholder="' + esc(t('ui.mount_ph')) + '">');
        h += '<button type="button" class="tc-btn sm ghost" id="ep-p-mflip">' + esc(t('ui.mount_flip')) + '</button>';
    } else if (f.kind === 'sections') {
        h += '<span class="ep-props-k">' + esc(t('ui.obj_section')) + '</span>';
        h += field(t('ui.section_name'), '<input class="tc-in" id="ep-p-sname" autocomplete="off" value="' + esc(o.name) + '">');
        h += field(t('ui.width_m'), numIn('ep-p-sw', o.r[2] - o.r[0], 0.5)) + field(t('ui.depth_m'), numIn('ep-p-sh', o.r[3] - o.r[1], 0.5));
    } else if (f.kind === 'routes') {
        h += '<span class="ep-props-k">' + esc(t('ui.obj_route')) + '</span>';
        h += field(t('ui.route_kind'), seg('rk', [['main', t('ui.exit_main')], ['emergency', t('ui.exit_emergency')]], o.kind));
    }
    h += '<button type="button" class="tc-btn sm ghost ep-del" id="ep-p-del">🗑️ ' + esc(t('ui.delete')) + '</button></div>';
    box.innerHTML = h;
    const lbl = $('#ep-p-text') || $('#ep-p-mlabel');
    if (lbl && !lbl.value && document.activeElement !== lbl) lbl.focus({ preventScroll: true });
}
function propsInput(e) {
    const id = editor.selection();
    const f = id ? Mo.findById(plan, id) : null;
    const el = e.target;
    if (!f) return;
    const o = f.obj;
    const live = e.type === 'input';
    if (el.id === 'ep-p-text') { o.text = el.value; editor.render(); if (!live) commit('label'); else scheduleCommit(); return; }
    if (el.id === 'ep-p-mlabel') { o.label = el.value; if (!live) commit('mount_label'); else scheduleCommit(); renderMountChips(); return; }
    if (el.id === 'ep-p-sname') { o.name = el.value; editor.render(); if (!live) commit('section_name'); else { scheduleCommit(); renderSheet(); } return; }
    if (live) return;
    const v = parseNum(el.value);
    if (el.id === 'ep-p-len' && v > 0.05) setWallLength(o, v);
    else if (el.id === 'ep-p-th' && v > 0.02 && v < 2) o.th = v;
    else if (el.id === 'ep-p-w' && v > 0.2) {
        const w = Mo.wallById(plan, o.wall);
        const L = w ? G.wallLength(w) : v;
        o.w = Math.min(v, L);
        o.t = Math.max(o.w / 2, Math.min(L - o.w / 2, o.t));
    } else if (el.id === 'ep-p-iw' && v > 0.2) o.w = v;
    else if (el.id === 'ep-p-ih' && v > 0.2) o.h = v;
    else if (el.id === 'ep-p-mgn') o.mgn = el.checked;
    else if (el.id === 'ep-p-sw' && v >= 1) o.r[2] = o.r[0] + v;
    else if (el.id === 'ep-p-sh' && v >= 1) o.r[3] = o.r[1] + v;
    else { renderProps(id); return; }
    commit('props');
}
let commitTimer = 0;
function scheduleCommit() { clearTimeout(commitTimer); commitTimer = setTimeout(() => commit('text'), 500); }
/* Длина стены: конец едет вдоль стены, и примыкающие стены едут за ним. */
function setWallLength(w, L) {
    const dir = G.wallDir(w);
    const old = w.b.slice();
    const nb = G.add(w.a, G.mul(dir, L)).map((v) => Math.round(v * 1000) / 1000);
    for (const o of plan.walls) {
        if (o === w) continue;
        if (G.dist(o.a, old) < 0.005) o.a = nb.slice();
        if (G.dist(o.b, old) < 0.005) o.b = nb.slice();
    }
    w.b = nb;
    for (const op of plan.openings) if (op.wall === w.id) op.t = Math.min(op.t, Math.max(op.w / 2, L - op.w / 2));
}
function propsClick(e) {
    const b = e.target.closest('button');
    if (!b) return;
    const id = editor.selection();
    const f = id ? Mo.findById(plan, id) : null;
    if (b.id === 'ep-len-go') { lengthGo(); return; }
    if (b.id === 'ep-finish') { editor.finish(); return; }
    if (!f) return;
    const o = f.obj;
    if (b.id === 'ep-p-del') { Mo.removeById(plan, id); editor.setSelection(null); commit('delete'); return; }
    if (b.dataset.wk) o.kind = b.dataset.wk, o.th = Mo.WALL_TH[o.kind];
    else if (b.dataset.ot) { o.type = b.dataset.ot; if (o.type === 'window') o.exit = ''; }
    else if (b.dataset.ex != null) { o.exit = b.dataset.ex; if (o.exit === 'main' || o.exit === 'emergency') o.type = o.type === 'window' ? 'door' : o.type; }
    else if (b.id === 'ep-p-flip') o.side = -(o.side || 1);
    else if (b.id === 'ep-p-hinge') o.hinge = o.hinge ? 0 : 1;
    else if (b.id === 'ep-p-rot') o.rot = ((+o.rot || 0) + 90) % 360;
    else if (b.id === 'ep-p-mflip') o.f = [-o.f[0], -o.f[1]];
    else if (b.dataset.rk) o.kind = b.dataset.rk;
    else return;
    commit('props');
}
function lengthGo() {
    const L = parseNum(($('#ep-len') || {}).value);
    const a = parseNum(($('#ep-ang') || {}).value);
    if (!(L > 0)) { refuse('len_bad'); return; }
    if (!editor.applyLength(L, isFinite(a) ? a : null)) refuse('len_need_start', 'info');
    const li = $('#ep-len');
    if (li) { li.value = ''; li.focus({ preventScroll: true }); }
}

/* ── инструменты ──────────────────────────────────────────────────────── */
function onTool(tool) {
    $$('#ep-tools [data-tool]').forEach((b) => { const on = b.dataset.tool === tool; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    const hint = $('#ep-hint');
    if (hint) hint.textContent = t('ui.hint.' + (tool === 'measure' && measurePurpose ? 'measure_' + measurePurpose : tool));
    $('#ep-palette-wrap').hidden = tool !== 'item';
    $('#ep-wallkind').hidden = tool !== 'wall';
    renderProps(editor.selection());
}

/* ── подложка: фото плана БТИ ─────────────────────────────────────────── */
let photo = null;          // {rgba, w, h, url, quad}
let pdfDoc = null;         // открытый PDF подложки (выбор страницы)
function isPdf(f) { return /\.pdf$/i.test(f.name || '') || f.type === 'application/pdf'; }
/* Поэтажный план часто приходит PDF-ом (выписка ЕГРН, план из CAD, скан из МФЦ).
   Страница рисуется в картинку той же величины, что фото (длинная сторона 2400 px),
   и дальше путь тот же: PDF — плоский лист, выпрямлять его не нужно. */
async function loadPdfjs() {
    if (window.__pdfjsLib) return window.__pdfjsLib;
    // Одна дверь pdf.js (PdfjsGoesThroughOneDoorTest): ждёт опоздавший загрузчик и отказывает его контрактом.
    return window.__pdfjs((C.assets && C.assets.pdfjs) || [{ mjs: '/vendor/pdfjs/4/pdf.min.mjs', worker: '/vendor/pdfjs/4/pdf.worker.min.mjs' }]);
}
async function pdfPageCanvas(n) {
    const page = await pdfDoc.getPage(n);
    const v1 = page.getViewport({ scale: 1 });
    const k = Math.min(6, 2400 / Math.max(v1.width, v1.height));
    const vp = page.getViewport({ scale: k });
    const cv = document.createElement('canvas');
    cv.width = Math.round(vp.width); cv.height = Math.round(vp.height);
    const cx = cv.getContext('2d', { willReadFrequently: true });
    cx.fillStyle = '#fff'; cx.fillRect(0, 0, cv.width, cv.height);
    await page.render({ canvasContext: cx, viewport: vp }).promise;
    return cv;
}
async function onPdf(file) {
    try {
        const lib = await loadPdfjs();
        const bytes = new Uint8Array(await file.arrayBuffer());
        if (pdfDoc) { try { pdfDoc.destroy(); } catch (_) { /* старый документ */ } }
        pdfDoc = await lib.getDocument({ data: bytes, isEvalSupported: false }).promise;
        ae('trace_pdf', { pages: Math.min(pdfDoc.numPages, 99) });
        renderPdfPages(1);
        await usePdfPage(1);
    } catch (e) {
        pdfDoc = null;
        window.__handleError && window.__handleError(e, 'evac_pdf', { say: t(e && e.name === 'PasswordException' ? 'ui.err_pdf_locked' : e && e.phase === 'script' ? 'ui.err_script' : 'ui.err_pdf') });
    }
}
function renderPdfPages(cur) {
    const box = $('#ep-pdf-pages');
    if (!box) return;
    const n = pdfDoc ? pdfDoc.numPages : 0;
    box.hidden = n < 2;
    if (n < 2) { box.innerHTML = ''; return; }
    const max = Math.min(n, 30);
    let h = '<span class="tc-f-l">' + esc(t('ui.pdf_page_pick', { n })) + '</span><div class="tc-chips">';
    for (let i = 1; i <= max; i++) h += '<button type="button" class="tc-chip' + (i === cur ? ' on' : '') + '" data-pdfpage="' + i + '" aria-pressed="' + (i === cur) + '">' + i + '</button>';
    box.innerHTML = h + '</div>';
}
async function usePdfPage(n) {
    const cv = await pdfPageCanvas(n);
    const rgba = cv.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, cv.width, cv.height).data;
    if (photo && photo.url) URL.revokeObjectURL(photo.url);
    photo = { rgba, w: cv.width, h: cv.height, url: null, quad: null, found: false, pdf: true };
    renderPdfPages(n);
    await straighten(true);
    $('#ep-trace-status').textContent = t('ui.trace_pdf');
}
async function onPhoto(file) {
    if (!file) return;
    stage('photo');
    ae('trace_photo', { size_mb: Math.round(file.size / 1e5) / 10, pdf: isPdf(file) ? 1 : 0 });
    if (isPdf(file)) { await onPdf(file); return; }
    if (pdfDoc) { try { pdfDoc.destroy(); } catch (_) { /* старый документ */ } pdfDoc = null; renderPdfPages(0); }
    try {
        const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const k = Math.min(1, 2400 / Math.max(bmp.width, bmp.height));
        const w = Math.round(bmp.width * k), h = Math.round(bmp.height * k);
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(bmp, 0, 0, w, h);
        bmp.close && bmp.close();
        const rgba = cx.getImageData(0, 0, w, h).data;
        let quad = null;
        try { quad = detectQuad(rgba, w, h); } catch (_) { quad = null; }
        if (!quad) quad = [{ x: w * 0.05, y: h * 0.05 }, { x: w * 0.95, y: h * 0.05 }, { x: w * 0.95, y: h * 0.95 }, { x: w * 0.05, y: h * 0.95 }];
        const blob = await window.__canvasBlob(cv, 'image/jpeg', 0.9);
        if (photo && photo.url) URL.revokeObjectURL(photo.url);
        photo = { rgba, w, h, url: URL.createObjectURL(blob), quad, found: !!quad };
        showCorners();
    } catch (e) {
        window.__handleError && window.__handleError(e, 'evac_photo', { say: t('ui.err_photo') });
    }
}
function showCorners() {
    const box = $('#ep-corners');
    box.hidden = false;
    const s = $('#ep-c-svg');
    s.setAttribute('viewBox', '0 0 ' + photo.w + ' ' + photo.h);
    const r = Math.max(photo.w, photo.h) / 70;
    const draw = () => {
        const q = photo.quad;
        s.innerHTML = '<image href="' + photo.url + '" x="0" y="0" width="' + photo.w + '" height="' + photo.h + '"/>' +
            '<polygon points="' + q.map((p) => p.x + ',' + p.y).join(' ') + '" class="ep-c-poly" stroke-width="' + r / 3 + '"/>' +
            q.map((p, i) => '<circle cx="' + p.x + '" cy="' + p.y + '" r="' + r + '" class="ep-c-h" data-c="' + i + '" stroke-width="' + r / 4 + '"/>').join('');
    };
    draw();
    let drag = -1;
    s.onpointerdown = (e) => {
        const c = e.target.closest('[data-c]');
        if (!c) return;
        drag = +c.getAttribute('data-c');
        try { s.setPointerCapture(e.pointerId); } catch (_) { /* старый браузер */ }
        e.preventDefault();
    };
    s.onpointermove = (e) => {
        if (drag < 0) return;
        const p = s.createSVGPoint(); p.x = e.clientX; p.y = e.clientY;
        const q = p.matrixTransform(s.getScreenCTM().inverse());
        photo.quad[drag] = { x: Math.max(0, Math.min(photo.w, q.x)), y: Math.max(0, Math.min(photo.h, q.y)) };
        draw();
        e.preventDefault();
    };
    s.onpointerup = s.onpointercancel = () => { drag = -1; };
    s.addEventListener('touchmove', (e) => { if (drag >= 0) e.preventDefault(); }, { passive: false });
    $('#ep-trace-status').textContent = t(photo.found ? 'ui.trace_found' : 'ui.trace_manual');
}
async function straighten(skip) {
    if (!photo) return;
    let out;
    if (skip) {
        out = { data: photo.rgba, w: photo.w, h: photo.h };
    } else {
        await new Promise((r) => setTimeout(r, 0));
        out = warp(photo.rgba, photo.w, photo.h, photo.quad, { maxEdge: 2200 });
    }
    const cv = document.createElement('canvas');
    cv.width = out.w; cv.height = out.h;
    cv.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(out.data.buffer || out.data), out.w, out.h), 0, 0);
    const blob = await window.__canvasBlob(cv, 'image/jpeg', 0.88);
    setUnderlay(blob, Tr.newUnderlay(out.w, out.h));
    $('#ep-corners').hidden = true;
    ae('trace_straighten', { skip: !!skip });
    commit('underlay');
    editor.fit();
    startMeasure('calib');
}
function setUnderlay(blob, u) {
    if (underlayUrl) URL.revokeObjectURL(underlayUrl);
    underlayBlob = blob;
    underlayUrl = blob ? URL.createObjectURL(blob) : null;
    if (u !== undefined) plan.underlay = u;
    syncUnderlayUi();
}
function startMeasure(purpose) {
    measurePurpose = purpose;
    editor.setTool('measure');
    $('#ep-measure-form').hidden = true;
}
function onMeasure(a, b) {
    if (!plan.underlay || !measurePurpose) { measurePurpose = null; editor.setTool('select'); return; }
    lastMeasure = { a: Tr.planToImg(plan.underlay, a), b: Tr.planToImg(plan.underlay, b), purpose: measurePurpose };
    const form = $('#ep-measure-form');
    form.hidden = false;
    $('#ep-measure-l').textContent = t(measurePurpose === 'calib' ? 'ui.measure_calib_q' : 'ui.measure_check_q');
    const inp = $('#ep-measure-d');
    inp.value = '';
    inp.focus({ preventScroll: true });
    editor.setTool('select');
}
function applyMeasure() {
    const d = parseNum($('#ep-measure-d').value);
    if (!(d > 0) || !lastMeasure) { refuse('len_bad'); return; }
    const u = plan.underlay;
    const out = $('#ep-calib-out');
    if (lastMeasure.purpose === 'calib') {
        plan.underlay = Tr.calibrate(u, lastMeasure.a, lastMeasure.b, d);
        out.className = 'tc-msg good';
        out.textContent = t('ui.calib_done', { d: nf(d, 2) });
        ae('calibrate');
        commit('calibrate');
        $('#ep-check-go').hidden = false;
    } else {
        const r = Tr.checkMeasure(u, lastMeasure.a, lastMeasure.b, d);
        const err = Math.abs(r.errPct);
        out.className = 'tc-msg ' + (err <= 1 ? 'good' : err <= 3 ? 'warn' : 'bad');
        out.textContent = t('ui.check_result', { m: nf(r.measured, 2), d: nf(d, 2), e: nf(err, 1) });
        const chk = { a: lastMeasure.a, b: lastMeasure.b, d };   // lastMeasure обнуляется ниже, кнопка жмётся позже
        const axes = u.calib ? Tr.solveAxes(u.calib, chk) : null;
        $('#ep-axes-go').hidden = !(axes && err > 0.5);
        $('#ep-axes-go').onclick = () => {
            plan.underlay = Tr.applyAxes(plan.underlay, axes);
            plan.underlay.check = chk;
            out.className = 'tc-msg good';
            out.textContent = t('ui.axes_done');
            $('#ep-axes-go').hidden = true;
            ae('calibrate_axes', { err: Math.round(err * 10) / 10 });
            commit('calibrate');
        };
        plan.underlay.check = { a: lastMeasure.a, b: lastMeasure.b, d };
        ae('check_measure', { err: Math.round(err * 10) / 10 });
        commit('check_measure');
    }
    $('#ep-measure-form').hidden = true;
    measurePurpose = null;
    lastMeasure = null;
    editor.setTool('wall');
}
function syncUnderlayUi() {
    const has = !!(plan.underlay && underlayUrl);
    $('#ep-u-ctl').hidden = !has;
    if (has) {
        $('#ep-u-op').value = String(Math.round((plan.underlay.opacity || 0.55) * 100));
        $('#ep-u-show').checked = plan.underlay.show !== false;
        $('#ep-check-go').hidden = !plan.underlay.calib;
    }
}

/* ── панели настроек ──────────────────────────────────────────────────── */
function syncControls() {
    const n = norms();
    const cs = $('#ep-country');
    if (cs && cs.value !== plan.country) cs.value = plan.country;
    $$('[data-kind]').forEach((b) => { const on = b.dataset.kind === plan.kind; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
    const fmt = formatOf();
    $$('[data-format]').forEach((b) => {
        const on = b.dataset.format === fmt;
        b.classList.toggle('on', on); b.setAttribute('aria-pressed', on);
        const min = n.profile.minSheet[plan.kind];
        const s = SHEETS[b.dataset.format];
        const short = min && s && (Math.max(s[0], s[1]) < Math.max(min[0], min[1]) - 0.05 || Math.min(s[0], s[1]) < Math.min(min[0], min[1]) - 0.05);
        b.classList.toggle('ep-short', !!short);
        b.title = short ? t('ui.fmt_short') : '';
    });
    $$('[data-mode]').forEach((b) => { const on = b.dataset.mode === (plan.sheet.mode || 'single'); b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
    $$('[data-tile]').forEach((b) => { const on = b.dataset.tile === (plan.sheet.tile || 'a4'); b.classList.toggle('on', on); b.setAttribute('aria-pressed', on); });
    $('#ep-tile-row').hidden = plan.sheet.mode !== 'tiles';
    const sc = $('#ep-scale'); if (sc) sc.value = String(plan.sheet.scale || 0);
    const m = plan.meta;
    const set = (id, v) => { const el = $(id); if (el && document.activeElement !== el && el.value !== String(v == null ? '' : v)) el.value = v == null ? '' : v; };
    set('#ep-m-object', m.object); set('#ep-m-address', m.address); set('#ep-m-floor', m.floor); set('#ep-m-section', m.section || '');
    set('#ep-m-developer', m.developer); set('#ep-m-date', m.date); set('#ep-m-rev', m.revision); set('#ep-m-type', m.objectType);
    set('#ep-m-lang', m.lang || LOC); set('#ep-m-lang2', m.lang2 || ''); set('#ep-m-approve', m.approve || '');
    set('#ep-m-sign', String(m.signMm || 9)); set('#ep-m-textscale', String(m.textScale || 1));
    const nt = $('#ep-m-night'); if (nt) nt.checked = !!m.night;
    $$('[data-eq]').forEach((c) => { c.checked = plan.equipment[c.dataset.eq] === true; });
    set('#ep-eq-power', plan.equipment.power || ''); set('#ep-eq-stop', plan.equipment.equipment || '');
    /* Поле «Секция» в данных листа — для плана без нарисованных границ; с границами
       имя секции берётся из неё самой. Инструмент «Секция» — только в секционном плане. */
    $('#ep-section-row').hidden = plan.kind !== 'section' || (plan.sections || []).length > 0;
    $$('[data-tool="section"]').forEach((b) => { b.hidden = plan.kind !== 'section'; });
    const basis = $('#ep-basis');
    if (basis) {
        basis.textContent = t(n.country.confidence === 'text' ? 'ui.basis_text' : 'ui.basis_retold', { basis: n.country.basis, date: fmtDate(n.country.checked) }) +
            (n.profile.id === 'ru' ? ' ' + t('ui.rules_until', { code: RU_RULES.code, edition: RU_RULES.edition, until: fmtDate(RU_RULES.validUntil) }) : '');
    }
    renderTextEditor();
    syncUnderlayUi();
}
function fmtDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return iso || '';
    try { return new Intl.DateTimeFormat(intl(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))); } catch (_) { return iso; }
}

/* Текст листа: разделы шаблона, пока человек их не тронул. */
let textRendered = '';
function renderTextEditor() {
    const box = $('#ep-text-list');
    if (!box) return;
    const secs = ctxFor(null).sections;
    const key = JSON.stringify(secs) + (plan.text ? '1' : '0');
    // Человек печатает в этом списке — не пересобирать его из-под курсора.
    if (box.contains(document.activeElement) || key === textRendered) return;
    textRendered = key;
    box.innerHTML = secs.map((s, i) => '<div class="ep-sec"><input class="tc-in ep-sec-h" data-sec="' + i + '" value="' + esc(s.h) + '" aria-label="' + esc(t('ui.text_heading')) + '">' +
        '<textarea class="tc-in ep-sec-t" data-sec="' + i + '" rows="' + Math.max(2, s.lines.length) + '" aria-label="' + esc(t('ui.text_lines')) + '">' + esc(s.lines.join('\n')) + '</textarea></div>').join('');
    $('#ep-text-state').textContent = t(plan.text ? 'ui.text_custom' : 'ui.text_template');
    $('#ep-text-reset').hidden = !plan.text;
}
function textEdited() {
    const secs = $$('.ep-sec').map((d) => ({ h: $('.ep-sec-h', d).value, lines: $('.ep-sec-t', d).value.split('\n').map((s) => s.trim()).filter(Boolean) }));
    plan.text = { sections: secs };
    scheduleCommit();
    renderSheet();
}

/* ── выдача ───────────────────────────────────────────────────────────── */
function fileBase() {
    const parts = [t('ui.file_base'), plan.meta.object || '', plan.meta.floor ? t('ui.file_floor', { n: plan.meta.floor }) : ''];
    return parts;
}
function nameFor(ext, extra) {
    const parts = fileBase().concat(extra ? [extra] : []);
    if (window.__outputName && window.__outputName.join) return window.__outputName.join(parts, ext);
    return parts.filter(Boolean).join('-').replace(/[\\/:*?"<>|;]+/g, '-') + '.' + ext;
}
async function loadPdfLib() {
    if (window.PDFLib) return window.PDFLib;
    const urls = (C.assets && C.assets.pdfLib) || ['/vendor/pdf-lib/pdf-lib.min.js'];
    if (window.__assetChain && window.__assetChain.loadScript) await window.__assetChain.loadScript(urls, 'pdf-lib');
    else await new Promise((res, rej) => { const s = document.createElement('script'); s.src = urls[0]; s.onload = res; s.onerror = () => rej(Object.assign(new Error('pdf-lib'), { phase: 'script' })); document.head.appendChild(s); });
    if (!window.PDFLib) throw Object.assign(new Error('pdf-lib absent'), { phase: 'script' });
    return window.PDFLib;
}
function tileStrings() {
    return { assemble_title: t('ui.assemble_title'), assemble_steps: get(C.t, 'ui.assemble_steps') || [], ruler: t('ui.ruler') };
}
function busy(btn, on) {
    if (!btn) return;
    if (on) { btn.dataset.html = btn.innerHTML; btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = t('ui.preparing'); }
    else { btn.disabled = false; btn.removeAttribute('aria-busy'); if (btn.dataset.html) btn.innerHTML = btn.dataset.html; }
}
async function exportPdf(kind) {
    if (!plan.walls.length) { refuse('need_plan'); return; }
    const btn = kind === 'zip' ? $('#ep-zip') : kind === 'png' ? $('#ep-png') : $('#ep-pdf');
    if (btn && btn.getAttribute('aria-busy') === 'true') return;
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, [plan.id]) : { commit() {}, abort() {} };
    if (!run) return;
    busy(btn, true);
    stage('exporting', { kind, mode: plan.sheet.mode });
    try {
        await ensureEngine();
        const all = layoutAll(true);
        const sheets = all.map((x) => x.sheet);
        const meta = { title: t('ui.file_base') + (plan.meta.object ? ' — ' + plan.meta.object : ''), subject: t('ui.pdf_subject'), lang: plan.meta.lang || LOC };
        let ok;
        if (kind === 'png') {
            ok = await exportPng(sheets[Math.min(sheetIdx, sheets.length - 1)]);
        } else {
            const PL = await loadPdfLib();
            const build = (list) => (plan.sheet.mode === 'tiles'
                ? buildTilesPdf(PL, list, engine, meta, { paper: plan.sheet.tile || 'a4', overlap: 10, strings: tileStrings(), paperLabel: t('ui.fmt.' + (plan.sheet.tile || 'a4')) })
                : buildSheetsPdf(PL, list, engine, meta));
            if (kind === 'zip' && sheets.length > 1) {
                const items = [];
                for (let i = 0; i < sheets.length; i++) {
                    const m = all[i].mount, sc = all[i].section;
                    const bytes = await build([sheets[i]]);
                    const tag = m && m.label ? ' ' + m.label : sc && String(sc.name || '').trim() ? ' ' + t('ui.section_n', { s: sc.name }) : '';
                    items.push({ name: nameFor('pdf', (i + 1) + tag), blob: new Blob([bytes], { type: 'application/pdf' }) });
                }
                if (!window.__batchDeliver && window.__BATCH_DELIVER_SRC && window.__loadScriptsOnce) await window.__loadScriptsOnce([window.__BATCH_DELIVER_SRC], 'batch-deliver');
                if (window.__batchDeliver) {
                    const r = await window.__batchDeliver.deliver(items, { zipName: nameFor('zip'), slug: SLUG, archiveOnly: true });
                    /* Отказ приходит как {via:'refused', written:0}: `via` у него есть, а файла нет.
                       Доставка — только записанные файлы (единица не списывается за отказ). */
                    ok = !!(r && r.written > 0);
                } else ok = await window.__downloadFile(items[0].blob, items[0].name);
            } else {
                const bytes = await build(sheets);
                ok = await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), nameFor('pdf'));
            }
        }
        if (ok === false) { run.abort(); stage('ready'); return; }
        run.commit();
        if (ed) ed.exported();
        const s = summarize(lastChecks);
        window.__aeToolComplete && window.__aeToolComplete({ kind, mode: plan.sheet.mode, sheets: sheets.length, profile: norms().profile.id, country: plan.country, format: formatOf(), errors: s.error, warns: s.warn });
        if (s.error) toast(t('ui.exported_with_errors', { n: s.error }), 'warning');
    } catch (e) {
        run.abort();
        const phase = e && e.phase;
        window.__handleError && window.__handleError(e, 'evac_export', { say: t(phase === 'fonts' ? 'ui.err_fonts' : phase === 'script' ? 'ui.err_script' : 'ui.err_export') });
    } finally {
        busy(btn, false);
    }
}
/* PNG — тот же лист, контуры текста; 150 dpi (лист 600×400 — 3543×2362 px). */
async function exportPng(sh) {
    const dpi = 150, k = dpi / 25.4;
    const W = Math.round(sh.w * k), H = Math.round(sh.h * k);
    const svg = sheetSvg(sh, { outline: engine && engine.outline, background: '#ffffff' }).replace('<svg ', '<svg width="' + W + '" height="' + H + '" ');
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
        await img.decode();
        const cv = document.createElement('canvas');
        cv.width = W; cv.height = H;
        const cx = cv.getContext('2d');
        cx.fillStyle = '#fff'; cx.fillRect(0, 0, W, H);
        cx.drawImage(img, 0, 0, W, H);
        const blob = await window.__canvasBlob(cv, 'image/png');
        return window.__downloadFile(blob, nameFor('png'));
    } finally { URL.revokeObjectURL(url); }
}
/* Напоминание о пересмотре (ГОСТ 34428 п. 9.3.9.19; ISO 23601 разд. 10): событие календаря
   через 12 месяцев от даты плана. Файл — не выдача плана, кап не трогает. */
function remindIcs() {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(plan.meta.date || ''));
    const base = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date();
    const at = new Date(base.getFullYear() + 1, base.getMonth(), base.getDate(), 10, 0, 0);
    const obj = String(plan.meta.object || '').trim();
    const parts = fileBase().concat([t('ui.remind_file')]);
    const filename = window.__outputName && window.__outputName.join ? window.__outputName.join(parts, '') : 'evacuation-plan-review';
    const n = downloadIcs([{ start: at, allDay: true, summary: t('ui.remind_summary', { obj: obj || t('ui.untitled') }).trim(), description: t('ui.remind_desc'), uid: plan.id + '-review' }],
        { name: t('ui.remind_summary', { obj: obj || t('ui.untitled') }), prodid: 'evacuation-plan', filename });
    ae('remind_ics', { ok: n ? 1 : 0 });
}

function renderExport(all) {
    const n = all.length;
    const zip = $('#ep-zip');
    if (zip) { zip.hidden = n < 2; zip.textContent = t('ui.btn_zip', { n }); }
    const pdf = $('#ep-pdf');
    if (pdf && pdf.getAttribute('aria-busy') !== 'true') pdf.textContent = n > 1 ? t('ui.btn_pdf_n', { n }) : t('ui.btn_pdf');
    const note = $('#ep-export-note');
    if (note) {
        const mode = plan.sheet.mode || 'single';
        let s = t('ui.note_' + mode, { fmt: t('ui.fmt.' + formatOf()) });
        if (mode === 'tiles') {
            const tl = all[0] && planTiles(all[0].sheet, plan.sheet.tile || 'a4', 10);
            if (tl) s = t('ui.note_tiles', { n: tl.count, paper: t('ui.fmt.' + (plan.sheet.tile || 'a4')), fmt: t('ui.fmt.' + formatOf()) });
        }
        note.textContent = s;
    }
}

/* ── черновик: план переживает F5 и крах ──────────────────────────────── */
function draftPayload() {
    if (!plan.walls.length && !underlayBlob) return null;
    return {
        state: { plan },
        files: underlayBlob ? [underlayBlob] : [],
        label: plan.meta.object || t('ui.untitled'),
        meta: { walls: plan.walls.length, mounts: plan.mounts.length },
    };
}
async function draftApply(d) {
    const p = d && d.state && d.state.plan;
    if (!p) return [];
    replacePlan(p);
    const f = d.files && d.files[0];
    if (f && plan.underlay) setUnderlay(f);
    else setUnderlay(null, plan.underlay && !f ? null : plan.underlay);
    ae('draft_restore');
    return [];
}
function draftFresh() {
    setUnderlay(null, null);
    replacePlan(Mo.newPlan({ country: plan.country }));
    plan.meta.lang = LOC;
}

/* ── проводка ─────────────────────────────────────────────────────────── */
function wire() {
    $('#ep-tools').addEventListener('click', (e) => {
        const b = e.target.closest('[data-tool]');
        if (b) { measurePurpose = null; editor.setTool(b.dataset.tool); }
    });
    $('#ep-palette').addEventListener('click', (e) => {
        const b = e.target.closest('[data-item]');
        if (!b) return;
        currentItem = b.dataset.item;
        $$('#ep-palette [data-item]').forEach((x) => x.classList.toggle('on', x === b));
    });
    $('#ep-wallkind').addEventListener('click', (e) => {
        const b = e.target.closest('[data-wkind]');
        if (!b) return;
        currentWallKind = b.dataset.wkind;
        $$('#ep-wallkind [data-wkind]').forEach((x) => { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b); });
    });
    $('#ep-rkind').addEventListener('click', (e) => {
        const b = e.target.closest('[data-rkind]');
        if (!b) return;
        currentRouteKind = b.dataset.rkind;
        $$('#ep-rkind [data-rkind]').forEach((x) => { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b); });
    });
    $('#ep-undo').addEventListener('click', undo);
    $('#ep-redo').addEventListener('click', redo);
    $('#ep-fit').addEventListener('click', () => editor.fit());
    const zi = $('#ep-zin'), zo = $('#ep-zout');
    if (window.__holdRepeat) {
        window.__holdRepeat(zi, () => { editor.zoom(1.2); });
        window.__holdRepeat(zo, () => { editor.zoom(1 / 1.2); });
    }
    zi.addEventListener('click', () => editor.zoom(1.2));
    zo.addEventListener('click', () => editor.zoom(1 / 1.2));
    $('#ep-grid').addEventListener('change', (e) => editor.setOpt('grid', e.target.checked));
    $('#ep-snap').addEventListener('change', (e) => editor.setOpt('snap', e.target.checked));
    const props = $('#ep-props');
    props.addEventListener('change', propsInput);
    props.addEventListener('input', propsInput);
    props.addEventListener('click', propsClick);
    props.addEventListener('keydown', (e) => { if (e.key === 'Enter' && (e.target.id === 'ep-len' || e.target.id === 'ep-ang')) { e.preventDefault(); lengthGo(); } });

    document.addEventListener('keydown', (e) => {
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
        if (!$('#ep').contains(document.activeElement) && document.activeElement !== document.body) return;
        const mod = e.ctrlKey || e.metaKey;
        if (mod && e.code === 'KeyZ' && !e.shiftKey) { e.preventDefault(); undo(); return; }
        if (mod && (e.code === 'KeyY' || (e.code === 'KeyZ' && e.shiftKey))) { e.preventDefault(); redo(); return; }
        if (e.key === 'Escape') { editor.cancel(); editor.setSelection(null); return; }
        if (e.key === 'Enter') { editor.finish(); return; }
        if ((e.key === 'Delete' || e.key === 'Backspace') && editor.selection()) {
            e.preventDefault();
            Mo.removeById(plan, editor.selection());
            editor.setSelection(null);
            commit('delete');
        }
    });

    // Начало: нарисовать / обвести фото / образец.
    $('#ep-start').closest('.tc-panel').addEventListener('click', (e) => {
        const b = e.target.closest('[data-start]');
        if (b) {
            $$('#ep-start [data-start]').forEach((x) => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-selected', on); });
            $$('[data-pane]').forEach((p) => { p.hidden = p.dataset.pane !== b.dataset.start; });
            return;
        }
        const s = e.target.closest('[data-sample]');
        if (s) openSample(s.dataset.sample);
    });
    $('#ep-room-add').addEventListener('click', () => {
        const w = parseNum($('#ep-rw').value), h = parseNum($('#ep-rh').value);
        if (!(w > 0.3 && h > 0.3 && w < 500 && h < 500)) { refuse('room_bad'); return; }
        let x = 0, y = 0;
        if (plan.walls.length) {
            const b = G.bbox(Mo.planPoints(plan));
            x = Math.round((b.x1 + 2) * 10) / 10; y = Math.round(b.y0 * 10) / 10;
        }
        Mo.addRect(plan, x, y, w, h, plan.walls.length ? 'int' : 'ext');
        ae('room_by_size');
        commit('room');
        editor.fit();
    });
    const zone = $('#ep-upload');
    if (zone && window.__bindUploaderFile) window.__bindUploaderFile(zone, (f) => onPhoto(f));
    $('#ep-corners-go').addEventListener('click', () => straighten(false));
    $('#ep-noskew').addEventListener('click', () => straighten(true));
    $('#ep-corners-cancel').addEventListener('click', () => { $('#ep-corners').hidden = true; });
    $('#ep-pdf-pages').addEventListener('click', (e) => {
        const b = e.target.closest('[data-pdfpage]');
        if (!b || !pdfDoc) return;
        ae('trace_pdf_page', { n: +b.dataset.pdfpage });
        usePdfPage(+b.dataset.pdfpage).catch((er) => window.__handleError && window.__handleError(er, 'evac_pdf_page', { say: t('ui.err_pdf') }));
    });
    $('#ep-calib-go').addEventListener('click', () => startMeasure('calib'));
    $('#ep-check-go').addEventListener('click', () => startMeasure('check'));
    $('#ep-measure-ok').addEventListener('click', applyMeasure);
    $('#ep-measure-d').addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); applyMeasure(); } });
    $('#ep-u-op').addEventListener('input', (e) => { if (plan.underlay) { plan.underlay.opacity = +e.target.value / 100; editor.render(); } });
    $('#ep-u-op').addEventListener('change', () => commit('underlay_op'));
    $('#ep-u-show').addEventListener('change', (e) => { if (plan.underlay) { plan.underlay.show = e.target.checked; commit('underlay_show'); } });
    $('#ep-u-del').addEventListener('click', () => { setUnderlay(null, null); commit('underlay_del'); });

    // Норма и лист.
    $('#ep-country').addEventListener('change', (e) => {
        plan.country = e.target.value;
        plan.sheet.format = '';
        ae('country', { c: plan.country });
        commit('country');
    });
    document.addEventListener('click', (e) => {
        const k = e.target.closest('#ep [data-kind]');
        if (k) {
            plan.kind = k.dataset.kind; plan.sheet.format = '';
            if (plan.kind !== 'section' && editor.getTool() === 'section') editor.setTool('select');
            commit('kind'); return;
        }
        const f = e.target.closest('#ep [data-format]');
        if (f) { plan.sheet.format = f.dataset.format; ae('format', { f: f.dataset.format }); commit('format'); return; }
        const m = e.target.closest('#ep [data-mode]');
        if (m) { plan.sheet.mode = m.dataset.mode; ae('mode', { m: m.dataset.mode }); commit('mode'); return; }
        const tl = e.target.closest('#ep [data-tile]');
        if (tl) { plan.sheet.tile = tl.dataset.tile; commit('tile'); return; }
        const sh = e.target.closest('#ep [data-sheet]');
        if (sh) { sheetIdx = +sh.dataset.sheet; renderSheet(); return; }
        const fx = e.target.closest('#ep [data-fix]');
        if (fx) { applyFix(fx); return; }
        const ck = e.target.closest('#ep [data-ck]');
        if (ck) jumpTo(lastChecks[+ck.dataset.ck]);
    });
    $('#ep-scale').addEventListener('change', (e) => { plan.sheet.scale = +e.target.value || 0; commit('scale'); });

    // Данные листа.
    const metaMap = { '#ep-m-object': 'object', '#ep-m-address': 'address', '#ep-m-floor': 'floor', '#ep-m-section': 'section', '#ep-m-developer': 'developer',
        '#ep-m-date': 'date', '#ep-m-rev': 'revision', '#ep-m-type': 'objectType', '#ep-m-lang': 'lang', '#ep-m-lang2': 'lang2', '#ep-m-approve': 'approve',
        '#ep-m-sign': 'signMm', '#ep-m-textscale': 'textScale' };
    for (const [sel, key] of Object.entries(metaMap)) {
        const el = $(sel);
        if (!el) continue;
        const h = (e) => {
            let v = el.value;
            if (key === 'signMm' || key === 'textScale') v = parseNum(v) || (key === 'signMm' ? 9 : 1);
            plan.meta[key] = v;
            if (key === 'lang' || key === 'lang2') { engine = null; enginePromise = null; }
            if (e.type === 'change') commit('meta_' + key); else { renderSheet(); scheduleCommit(); }
        };
        el.addEventListener('change', h);
        if (el.tagName === 'INPUT' && el.type === 'text' || el.tagName === 'TEXTAREA') el.addEventListener('input', h);
    }
    $('#ep-m-night').addEventListener('change', (e) => { plan.meta.night = e.target.checked; commit('night'); });
    $$('[data-eq]').forEach((c) => c.addEventListener('change', () => { plan.equipment[c.dataset.eq] = c.checked; commit('equipment'); }));
    for (const [sel, key] of [['#ep-eq-power', 'power'], ['#ep-eq-stop', 'equipment']]) {
        const el = $(sel);
        el.addEventListener('input', () => { plan.equipment[key] = el.value; renderSheet(); scheduleCommit(); });
    }
    $('#ep-text-list').addEventListener('input', textEdited);
    $('#ep-text-reset').addEventListener('click', () => { plan.text = null; textRendered = ''; commit('text_reset'); });

    // Пути.
    $('#ep-routes-ok').addEventListener('click', () => { if (an) { plan.routes.confirmed = an.sig; ae('routes_confirm'); commit('routes_confirm'); } });
    $('#ep-routes-manual').addEventListener('click', () => {
        if (!an) return;
        const tr = an.tree(plan.mounts.map((m) => m.id));
        plan.routes.manual = [];
        for (const k of ['main', 'emergency']) for (const b of tr[k]) plan.routes.manual.push({ id: Mo.uid('r'), pts: b.pts.map((p) => [Math.round(p[0] * 100) / 100, Math.round(p[1] * 100) / 100]), kind: k });
        plan.routes.mode = 'manual';
        ae('routes_manual');
        commit('routes_manual');
    });
    $('#ep-routes-auto').addEventListener('click', () => { plan.routes.mode = 'auto'; plan.routes.manual = []; plan.routes.confirmed = ''; commit('routes_auto'); });

    // Лист крупно.
    $('#ep-big-go').addEventListener('click', openBig);
    $('#ep-big-close').addEventListener('click', () => { if (bigWs) bigWs.exit(); });
    $('#ep-big-in').addEventListener('click', () => zoomBig(1.6));
    $('#ep-big-out').addEventListener('click', () => zoomBig(1 / 1.6));

    // Выдача.
    $('#ep-pdf').addEventListener('click', () => exportPdf('pdf'));
    $('#ep-zip').addEventListener('click', () => exportPdf('zip'));
    $('#ep-png').addEventListener('click', () => exportPdf('png'));
    $('#ep-remind').addEventListener('click', remindIcs);
}

function openSample(name) {
    const fn = Mo.SAMPLES[name];
    if (!fn) return;
    const p = fn((k) => t('samples.' + k));
    p.country = plan.country;
    p.meta.lang = LOC;
    setUnderlay(null, null);
    replacePlan(p);
    if (ed) ed.touch(true);
    ae('sample_open', { s: name });
    stage('sample');
}
function jumpTo(r) {
    if (!r) return;
    const ids = r.params.ids || [];
    if (ids.length) { editor.setSelection(ids[0]); return; }
    const sd = r.params.seeds && r.params.seeds[0];
    if (sd) editor.centerOn(sd);
}

function boot() {
    wire();
    const pal = $('#ep-palette');
    pal.innerHTML = PALETTE.map(([g, ids]) => '<div class="ep-pal-g"><span class="tc-f-l">' + esc(t('ui.pal.' + g)) + '</span><div class="ep-pal-row">' +
        ids.map((id) => '<button type="button" class="ep-pal-b' + (id === currentItem ? ' on' : '') + '" data-item="' + id + '" title="' + esc(t('sheet_sym.' + id)) + '" aria-label="' + esc(t('sheet_sym.' + id)) + '">' + iconSvg(id) + '</button>').join('') +
        '</div></div>').join('');
    editor.setTool('select');
    changed(true);
    stage('ready');
    if (window.__toolDraft && window.__toolDraft.editor && $('#epDrafts')) {
        ed = window.__toolDraft.editor({
            slug: SLUG, root: 'epDrafts', icon: '🧯',
            payload: draftPayload, apply: draftApply, fresh: draftFresh,
            started: () => plan.walls.length > 0,
        });
        ed.boot();
    }
    // Вкладку старта («Обвести фото», «Образец») нажали раньше, чем проснулся скрипт: выполняем то же нажатие
    // (docs/why/primitives/early-action.md). Клик идёт в обработчик панели старта, как живой.
    if (window.__earlyAction) {
        window.__earlyAction.claim('evacuation-plan-start', (a) => {
            const b = document.querySelector('#ep-start [data-start="' + a + '"]');
            if (b) b.click();
        });
    }
}
function iconSvg(id) {
    return '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">' + iconParts(id, 12, 12, 22).map((p) => p.fill
        ? '<path d="' + p.d + '" fill="' + rgbOf(p.fill) + '"/>'
        : '<path d="' + p.d + '" fill="none" stroke="' + rgbOf(p.stroke) + '" stroke-width="' + p.w + '" stroke-linecap="' + (p.cap || 'butt') + '" stroke-linejoin="round"/>').join('') + '</svg>';
}

boot();
// Для стенда: состояние страницы без DOM-разбора.
window.__evacDebug = { get plan() { return plan; }, get an() { return an; }, layoutAll, runChecks: () => lastChecks, replacePlan, openSample, editor, mountTheta, SCALES, NORMS_CHECKED };
